import { ButtonGroup, Tooltip, IconButton } from '@chakra-ui/react';
import { BsPaintBucket } from 'react-icons/bs';
import { MdFormatColorText } from 'react-icons/md';
import { RiTodoLine } from 'react-icons/ri';
import { TbSelect } from 'react-icons/tb';
import { FilterTabs, UseFilterConfigs } from '../useFilterConfigs';
import { FiSettings } from 'react-icons/fi';
import { FilterValueMode } from '../../../../types';
import _ from 'lodash';

interface FilterByTabsProps
  extends Pick<
    UseFilterConfigs,
    | 'condition'
    | 'textColor'
    | 'fillColor'
    | 'filterValues'
    | 'valueMode'
    | 'errors'
  > {
  switchTab: (tab: FilterTabs) => void;
  activeTab: FilterTabs;
}
const FilterByTabs: React.FC<FilterByTabsProps> = ({
  activeTab,
  switchTab,
  ...props
}) => {
  const tabButtonView = (key: FilterTabs) => {
    const isActive = key === activeTab;
    const Icon = getFilterByIcon(key);
    const tabStatus = getTabStatus(key, props);
    return (
      <IconButton
        variant={getVariant(tabStatus)}
        colorScheme={getColorScheme(tabStatus)}
        padding="5px 15px"
        aria-label={`Filter By ${key}`}
        icon={<Icon />}
        rounded="2xl"
        isActive={isActive}
        onClick={() => switchTab(key)}
      />
    );
  };

  return (
    <ButtonGroup size="sm" rounded="2xl">
      {Object.values(FilterTabs).map((key) => (
        <Tooltip
          key={key}
          p={3}
          label={`Filter By ${key}`}
          borderRadius={10}
          placement="top"
          hasArrow={true}
          textTransform="capitalize"
          children={tabButtonView(key)}
        />
      ))}
    </ButtonGroup>
  );
};

const getFilterByIcon = (key: string) => {
  switch (key) {
    case FilterTabs.Value:
      return TbSelect;
    case FilterTabs.Condition:
      return RiTodoLine;
    case FilterTabs.TextColor:
      return MdFormatColorText;
    case FilterTabs.FillColor:
      return BsPaintBucket;
    default:
      return FiSettings;
  }
};

interface TabStatus {
  applied: boolean;
  error: boolean;
}
const getTabStatus = (
  key: string,
  props: Omit<FilterByTabsProps, 'switchTab' | 'activeTab'>
) => {
  const status: TabStatus = {
    applied: false,
    error: false,
  };
  switch (key) {
    case FilterTabs.Value:
      if (
        props.valueMode === FilterValueMode.Select ||
        !!Object.keys(props.filterValues).length
      )
        status.applied = true;
      break;
    case FilterTabs.Condition:
      if (props.condition) status.applied = true;
      if (!_.isEmpty(props.errors)) status.error = true;
      break;
    case FilterTabs.TextColor:
      if (props.textColor) status.applied = true;
      break;
    case FilterTabs.FillColor:
      if (props.fillColor) status.applied = true;
      break;
  }
  return status;
};

const getColorScheme = (tabStatus: TabStatus) => {
  if (tabStatus.error) {
    return 'red';
  } else if (tabStatus.applied) {
    return 'green';
  }

  return 'gray';
};

const getVariant = (tabStatus: TabStatus) => {
  if (tabStatus.error || tabStatus.applied) return 'outline';

  return 'ghost';
};

export default FilterByTabs;
